import React, { useState, useEffect } from "react";
import { DataTable, Badge, Modal, EmptyState as Empty } from "@/components/ui";
import { fmtDate } from "@/lib/formatters";
import { Eye, Check, X, Package } from "lucide-react";
import API from "@/lib/API";
import toast from "react-hot-toast";
import { FilterSection } from "@/components/ui/FilterSection";

export default function MaterialApprovalTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [purchaseMasters, setPurchaseMasters] = useState([]);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ date: "all", fromDate: "", toDate: "", status: "AWAITING_OWNER_APPROVAL" });

  const STATUS_OPTIONS = [
    { value: "AWAITING_OWNER_APPROVAL", label: "Pending" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
    { value: "DISPATCHED", label: "Dispatched" },
    { value: "DELIVERED", label: "Delivered" }
  ];

  const handleSetFilters = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await API.get("/final-inventory/material-requests?ownerView=true");
      const list = res.data?.data || res.data?.requests || res.data || [];
      setRequests(list);
    } catch (err) {
      console.error("Failed to fetch material requests:", err);
      toast.error("Failed to fetch material requests: " + (err.message || String(err)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchPurchaseMasters();
  }, []);

  const fetchPurchaseMasters = async () => {
    try {
      const res = await API.get("/v1/purchase-master?limit=5000");
      setPurchaseMasters(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to fetch purchase masters:", err);
    }
  };

  const handleActionClick = (req, type) => {
    setSelectedRequest(req);
    setActionType(type);
    setRemarks("");
    setIsModalOpen(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      if (actionType === "reject") {
        await API.put(`/final-inventory/material-requests/${selectedRequest._id}/status`, {
          status: "REJECTED"
        });
        toast.success("Material request rejected successfully");
      }
      setIsModalOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      console.error("Material action failed:", err);
      toast.error(err.response?.data?.message || `Failed to ${actionType} request`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDirectApprove = async (req) => {
    try {
      await API.put(`/final-inventory/material-requests/${req._id}/owner-approve`);
      toast.success("Material request approved successfully");
      fetchRequests();
    } catch (err) {
      console.error("Material approval action failed:", err);
      toast.error(err.response?.data?.message || `Failed to approve request`);
    }
  };

  const filteredRequests = React.useMemo(() => {
    let result = [...requests];

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(r => 
        r.requestNo?.toLowerCase().includes(lower) ||
        r.project?.customerName?.toLowerCase().includes(lower) ||
        r.project?.projectId?.toLowerCase().includes(lower) ||
        r.installationTrade?.toLowerCase().includes(lower) ||
        r.notes?.toLowerCase().includes(lower)
      );
    }

    const { date, fromDate, toDate, status } = filters;

    if (status && status !== "all") {
      result = result.filter(r => String(r.status).toUpperCase() === String(status).toUpperCase());
    }

    if (date !== "all" || fromDate || toDate) {
      let startDate = null;
      let endDate = null;

      const now = new Date();
      if (date && date !== "all") {
        startDate = new Date();
        switch (date) {
          case "today": startDate.setHours(0, 0, 0, 0); break;
          case "this_week": case "week": startDate.setDate(now.getDate() - 7); break;
          case "this_month": case "month": startDate.setDate(now.getDate() - 30); break;
          case "this_year": case "year": startDate.setDate(now.getDate() - 365); break;
        }
        endDate = now;
      } else {
        if (fromDate) startDate = new Date(fromDate);
        if (toDate) endDate = new Date(toDate);
      }

      if (startDate || endDate) {
        result = result.filter((r) => {
          const createdAt = new Date(r.createdAt);
          if (startDate && createdAt < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (createdAt > end) return false;
          }
          return true;
        });
      }
    }

    return result;
  }, [requests, search, filters]);

  const columns = [
    {
      header: "Request No",
      key: "requestNo",
      render: (r) => (
        <span className="text-[10px] font-bold text-brand-orange whitespace-nowrap">
          #{r.requestNo || "—"}
        </span>
      ),
    },
    {
      header: "Project ID",
      key: "projectId",
      render: (r) => (
        <span className="text-[10px] text-brand-blue font-bold uppercase tracking-wider">
          {r.project?.projectId || "—"}
        </span>
      ),
    },
    {
      header: "Customer Name",
      key: "customerName",
      render: (r) => (
        <span className="text-brand-dark tracking-tight text-[12px] font-medium leading-tight truncate">
          {r.project?.customerName || "—"}
        </span>
      ),
    },
    {
      header: "Department",
      key: "installationTrade",
      render: (r) => (
        <span className="text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-slate-100 text-slate-600">
          {r.installationTrade || "General"}
        </span>
      ),
    },
    {
      header: "Requested Items",
      key: "requestedItems",
      render: (r) => {
        const itemNames = (r.items || []).map(i => i.itemName || i.item?.itemName || "Unnamed Item");
        if (itemNames.length === 0) return <span className="text-slate-400 text-[11px]">—</span>;
        
        const display = itemNames.slice(0, 2).join(", ");
        const more = itemNames.length > 2 ? ` + ${itemNames.length - 2} more` : "";
        
        return (
          <div className="flex flex-col max-w-[200px]" title={itemNames.join(", ")}>
            <span className="text-[11px] font-medium text-slate-700 truncate">
              {display}
            </span>
            {more && (
              <span className="text-[10px] font-semibold text-brand-orange mt-0.5">
                {more}
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: "Items Count",
      key: "itemsCount",
      render: (r) => (
        <span className="text-[12px] font-semibold text-slate-700">
          {r.items?.length || 0} items
        </span>
      ),
    },
    {
      header: "Date",
      key: "createdAt",
      render: (r) => (
        <span className="text-[11px] font-medium text-slate-600">
          {fmtDate(r.createdAt)}
        </span>
      ),
    },
    {
      header: "Requested By",
      key: "requestedBy",
      render: (r) => (
        <span className="text-[11px] font-bold text-slate-600 uppercase whitespace-nowrap">
          {r.requestedBy?.name || "Staff"}
        </span>
      ),
    },
    {
      header: "Status",
      key: "status",
      render: (r) => {
        const s = r.status || "PENDING";
        const colors = {
          PENDING: "bg-amber-100 text-amber-700 border-amber-200",
          AWAITING_OWNER_APPROVAL: "bg-amber-100 text-amber-700 border-amber-200", // Visually same as PENDING
          APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
          DISPATCHED: "bg-cyan-100 text-cyan-700 border-cyan-200",
          DELIVERED: "bg-emerald-100 text-emerald-700 border-emerald-200",
          REJECTED: "bg-red-100 text-red-700 border-red-200",
        };
        const displayText = s === 'AWAITING_OWNER_APPROVAL' ? 'PENDING' : s.replace(/_/g, " ");
        return (
          <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider border whitespace-nowrap ${colors[s] || colors.PENDING}`}>
            {displayText}
          </span>
        );
      },
    },
    {
      header: "Actions",
      key: "actions",
      render: (r) => {
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleActionClick(r, "view")}
              className="p-1 text-slate-500 hover:bg-slate-50 rounded transition-colors cursor-pointer"
              title="View Details"
            >
              <Eye size={18} strokeWidth={2.5} />
            </button>
            {(r.status === "AWAITING_OWNER_APPROVAL" || r.status === "PENDING") && (
              <>
                <button
                  onClick={() => handleDirectApprove(r)}
                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors cursor-pointer"
                  title="Approve Request"
                >
                  <Check size={18} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => handleActionClick(r, "reject")}
                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                  title="Reject Request"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0">
        <FilterSection
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={handleSetFilters}
          showExport={false}
          statusOptions={STATUS_OPTIONS}
          statusPlaceholder="All Statuses"
        />
      </div>

      <div className="w-full h-full bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredRequests}
          isLoading={loading}
          className="border-none shadow-none rounded-none"
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          actionType === 'view'
            ? 'Material Request Details'
            : actionType === 'approve'
            ? 'Approve Material Request'
            : 'Reject Material Request'
        }
        size="max-w-lg"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-3 p-4 bg-slate-50/50 rounded-xl border border-slate-100">
            <div className="flex justify-between items-center pb-2 border-b border-slate-100">
              <span className="text-[11px] font-bold text-slate-600 uppercase">Request No</span>
              <span className="text-[11px] font-bold text-brand-orange uppercase">#{selectedRequest?.requestNo}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[13px] font-medium text-slate-500">Project</span>
              <span className="text-[13px] font-bold text-slate-800">{selectedRequest?.project?.projectId} - {selectedRequest?.project?.customerName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[13px] font-medium text-slate-500">Department</span>
              <span className="text-[13px] font-bold text-slate-800 capitalize">{selectedRequest?.installationTrade || "General"}</span>
            </div>
            {actionType === 'view' && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-slate-500">Requested By</span>
                  <span className="text-[13px] font-bold text-slate-800">{selectedRequest?.requestedBy?.name || "Staff"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-slate-500">Requested Date</span>
                  <span className="text-[13px] font-bold text-slate-800">{selectedRequest?.createdAt ? fmtDate(selectedRequest.createdAt) : "—"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] font-medium text-slate-500">Status</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-brand-blue">{selectedRequest?.status?.replace(/_/g, " ")}</span>
                </div>
                {selectedRequest?.notes && (
                  <div className="flex flex-col gap-1 pt-2 border-t border-slate-100">
                    <span className="text-[11px] font-bold text-slate-500 uppercase">Notes / Remarks</span>
                    <span className="text-[12px] text-slate-700 italic font-medium">"{selectedRequest.notes}"</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Render Items List with Total Table Details */}
          <div className="border border-slate-200 rounded-lg max-h-60 overflow-y-auto shadow-sm bg-white">
            <table className="w-full text-left border-collapse whitespace-nowrap">
              <thead className="bg-slate-50 sticky top-0 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <tr className="divide-x divide-slate-100">
                  <th className="px-3 py-2">Material</th>
                  <th className="px-3 py-2 text-center">Requested</th>
                  <th className="px-3 py-2 text-center">Dispatched</th>
                  <th className="px-3 py-2 text-center">Received</th>
                  <th className="px-3 py-2 text-center">Pending</th>
                  <th className="px-3 py-2 text-center">Available Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-[11px] font-medium text-slate-700">
                {selectedRequest?.items?.map((it, idx) => {
                  const itemData = it.inventoryId?.item || it.item || it;
                  const requestedQty = Number(it.quantity || it.qty || it.requiredQty || 0);
                  const dispatchedQty = Number(it.dispatchedQuantity || it.dispatchedQty || 0);
                  const receivedQty = Number(it.receivedQuantity || it.receivedQty || it.deliveredQuantity || it.deliveredQty || 0);
                  
                  const nameToMatch = (it.itemName || itemData.name || itemData.itemName || "").toLowerCase().trim();
                  const pmItem = purchaseMasters.find(pm => {
                    const desc = (pm.description || "").toLowerCase().trim();
                    const info = (pm.information || "").toLowerCase().trim();
                    const art = (pm.articleId || "").toLowerCase().trim();
                    const shut = (pm.shutterType || "").toLowerCase().trim();
                    const inter = (pm.internalType || "").toLowerCase().trim();
                    const cat = (pm.category || "").toLowerCase().trim();
                    const subCat = (pm.subCategory || "").toLowerCase().trim();
                    
                    return desc === nameToMatch || 
                           info === nameToMatch || 
                           art === nameToMatch || 
                           shut === nameToMatch || 
                           inter === nameToMatch ||
                           cat === nameToMatch ||
                           subCat === nameToMatch;
                  });
                  const availableQty = pmItem ? Number(pmItem.availableQty || 0) : Number(it.inventoryId?.stock || itemData.availableQty || 0);
                  
                  const isFullyDelivered = receivedQty >= requestedQty;

                  return (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors divide-x divide-slate-50">
                      <td className="px-3 py-2 font-semibold text-slate-800">
                        {it.itemName || itemData.name || itemData.itemName || "Unknown Item"}
                        <div className="text-[9px] text-slate-400 font-bold uppercase">{itemData.unit || itemData.units || 'units'}</div>
                      </td>
                      <td className="px-3 py-2 font-bold text-slate-800 text-center">{requestedQty}</td>
                      <td className="px-3 py-2 font-bold text-brand-blue text-center">{dispatchedQty}</td>
                      <td className="px-3 py-2 font-bold text-slate-800 text-center">{receivedQty}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${isFullyDelivered ? 'text-emerald-500' : 'text-amber-600 bg-amber-50 border border-amber-200'}`}>
                            {!isFullyDelivered ? `${Math.max(0, requestedQty - receivedQty)} missing` : 'All met'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center font-bold text-indigo-600">
                        {availableQty}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {actionType !== 'view' && (
            <p className="text-sm text-slate-600">
              Are you sure you want to {actionType} this material request?
            </p>
          )}

          <div className="flex gap-3 pt-4">
            {actionType === 'view' ? (
              <button
                onClick={() => setIsModalOpen(false)}
                className="btn-secondary flex-1 cursor-pointer bg-slate-800 text-white hover:bg-slate-700"
              >
                Close Details
              </button>
            ) : (
              <>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="btn-secondary flex-1 cursor-pointer"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  className={`btn-primary flex-1 flex justify-center items-center cursor-pointer ${
                    actionType === "reject" ? "bg-red-600 hover:bg-red-700 border-red-600" : "bg-brand-orange hover:bg-orange-600 border-brand-orange"
                  }`}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  ) : (
                    actionType === "approve" ? "Approve" : "Reject Request"
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
